/**
 * CourseViewer v4 — Premium course presentation
 *
 * Cinematic hero · resume flow · image gallery · citations · lesson TOC
 * Mobile: bottom-sheet nav · fixed bottom bar · swipe gestures · tools drawer
 * Desktop: sidebar + focus mode · floating nav · reading progress · rich markdown
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './CourseViewer.css';
import {
  resolveCourseThumbnail,
  getInstructorName,
  formatCourseDuration,
  formatCategoryLabel,
} from '../studentPortalUtils';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import 'katex/dist/katex.min.css';
import {
  BookOpen, CheckCircle, Circle, Clock, ArrowLeft, ChevronDown, ChevronRight,
  ChevronLeft, Play, FileText, Code, HelpCircle, Lightbulb, Sparkles, Menu, X,
  Target, Layers, GraduationCap, AlertCircle, Brain, Search, StickyNote,
  ZoomIn, ZoomOut, Maximize2, Minimize2, MessageSquare, Trophy, Flag,
  BookmarkPlus, Bookmark, Copy, MoreHorizontal, Award, Image as ImageIcon,
  ExternalLink, User, List, FlaskConical,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { benchPath, isLabLesson } from '../../../data/studioLabs';
import {
  Box, LinearProgress, SwipeableDrawer, Drawer, Collapse, IconButton, Typography,
  Button, Chip, CircularProgress, Alert, TextField, InputAdornment, Tooltip, useMediaQuery,
} from '@mui/material';
import MermaidDiagram from '../../../components/Academy/MermaidDiagram';
import QuizLesson from '../../../components/Academy/QuizLesson';
import AudioLessonPlayer from '../../../components/Academy/AudioLessonPlayer';
import AssignmentSubmission from '../../../components/Academy/AssignmentSubmission';
import CapstoneSubmission from '../../../components/Academy/CapstoneSubmission';

/* ═══════════════════════════════════════════════════════════════════════════════
   CONSTANTS & DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════════ */

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  'https://qorenova-backend-production.up.railway.app';

const SIDEBAR_W = 300;

const BG        = '#09090b';
const BG_RAISED = '#0f0f12';
const HEADING   = '#fafafa';
const BODY      = '#d4d4d8';
const SECONDARY = '#a1a1aa';
const MUTED     = '#71717a';
const FAINT     = '#52525b';
const CARD_BG   = 'rgba(255,255,255,0.025)';
const CARD_BDR  = '1px solid rgba(255,255,255,0.08)';
const CARD_RAD  = '14px';
const ACCENT    = '#a78bfa';
const ACCENT_DK = '#7c3aed';
const GREEN     = '#4ade80';
const GOLD      = '#fbbf24';

/* ═══════════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════════ */

const readingTime = (text = '') => {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

const noteKey     = (courseId, si, li) => `cv_note_${courseId}_${si}_${li}`;
const bookmarkKey = (courseId) => `cv_bookmarks_${courseId}`;
const resumeKey   = (courseId) => `cv_resume_${courseId}`;

const COLOR_PALETTE = ['#f59e0b', '#8b5cf6', '#10b981', '#3b82f6', '#ef4444', '#06b6d4', '#ec4899'];

const pickCourseColor = (key = '') => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return COLOR_PALETTE[Math.abs(h) % COLOR_PALETTE.length];
};

const slugify = (text = '') =>
  text.toLowerCase().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').slice(0, 60);

const extractHeadings = (content = '') =>
  content.split('\n').reduce((acc, line, i) => {
    const m = /^(#{2,3})\s+(.+)$/.exec(line.trim());
    if (!m) return acc;
    const text = m[2].replace(/\*\*/g, '').trim();
    acc.push({ level: m[1].length, text, id: `cv-h-${slugify(text)}-${i}` });
    return acc;
  }, []);

const getResumeLesson = (curriculum = [], completed = new Set(), courseId) => {
  try {
    const saved = JSON.parse(localStorage.getItem(resumeKey(courseId)) || 'null');
    if (saved && curriculum[saved.sectionIndex]?.lessons?.[saved.lessonIndex]) return saved;
  } catch { /* ignore */ }
  for (let si = 0; si < curriculum.length; si++) {
    const lessons = curriculum[si].lessons || [];
    for (let li = 0; li < lessons.length; li++) {
      if (!completed.has(`${si}-${li}`)) return { sectionIndex: si, lessonIndex: li };
    }
  }
  return { sectionIndex: 0, lessonIndex: 0 };
};

const celebrate = (pct) => {
  if (pct === 100) {
    confetti({ particleCount: 200, spread: 100, origin: { y: 0.5 }, colors: ['#a78bfa','#7c3aed','#4ade80','#fbbf24','#f472b6'] });
    setTimeout(() => confetti({ particleCount: 100, spread: 60, origin: { y: 0.5 } }), 500);
    setTimeout(() => confetti({ particleCount: 60, spread: 40, origin: { y: 0.45 } }), 1000);
  } else if (pct > 0 && pct % 25 === 0) {
    confetti({ particleCount: 80, spread: 60, origin: { y: 0.6 }, colors: ['#a78bfa','#4ade80'] });
  }
};

const LESSON_TYPE = {
  video:      { icon: Play,          color: '#f87171', label: 'Video'      },
  reading:    { icon: FileText,      color: '#60a5fa', label: 'Reading'    },
  text:       { icon: FileText,      color: '#60a5fa', label: 'Reading'    },
  code:       { icon: Code,          color: '#34d399', label: 'Code Lab'   },
  assignment: { icon: HelpCircle,    color: '#fbbf24', label: 'Assignment' },
  quiz:       { icon: Brain,         color: '#f472b6', label: 'Quiz'       },
  lab:        { icon: FlaskConical,  color: '#22d3ee', label: 'Lab bench'  },
  project:    { icon: Trophy,        color: '#fbbf24', label: 'Defence'    },
  default:    { icon: BookOpen,      color: ACCENT,    label: 'Lesson'     },
};
const getLessonType = (type) => LESSON_TYPE[type] || LESSON_TYPE.default;

/* ═══════════════════════════════════════════════════════════════════════════════
   CODE BLOCK — standalone component so it can own copy state
   ═══════════════════════════════════════════════════════════════════════════════ */

const CodeBlock = ({ inline, className, children, ...props }) => {
  const [copied, setCopied] = useState(false);
  const match    = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  const code     = String(children).replace(/\n$/, '');

  if (!inline && language === 'mermaid') {
    return <MermaidDiagram code={code} />;
  }

  if (inline || !language) return <code className={className} {...props}>{children}</code>;

  const handleCopy = () => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ position: 'relative', my: 2.5, '&:hover .copy-btn': { opacity: 1 } }}>
      <Tooltip title={copied ? 'Copied!' : 'Copy code'} placement="left">
        <IconButton
          className="copy-btn"
          size="small"
          onClick={handleCopy}
          sx={{
            position: 'absolute', top: 10, right: 10, zIndex: 2,
            opacity: { xs: 1, md: 0 }, transition: 'opacity 0.2s',
            bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            color: copied ? GREEN : 'rgba(255,255,255,0.5)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)', color: copied ? GREEN : HEADING },
          }}
        >
          {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
        </IconButton>
      </Tooltip>
      {language && (
        <Typography sx={{
          position: 'absolute', top: 10, left: 16, zIndex: 2,
          fontSize: 10, color: FAINT, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          {language}
        </Typography>
      )}
      <SyntaxHighlighter
        style={vscDarkPlus}
        language={language}
        PreTag="div"
        customStyle={{
          borderRadius: '12px', padding: '36px 18px 18px', fontSize: '13px',
          border: '1px solid rgba(255,255,255,0.06)', margin: 0,
        }}
        {...props}
      >
        {code}
      </SyntaxHighlighter>
    </Box>
  );
};

const stripYoutubeMarkers = (content = '') =>
  content.replace(/<!--\s*youtube:[\w-]+\s*-->/gi, '').trim();

const RichImage = ({ src, alt, title }) => (
  <Box
    component="figure"
    sx={{
      my: 2.5,
      m: 0,
      borderRadius: '14px',
      overflow: 'hidden',
      border: CARD_BDR,
      bgcolor: CARD_BG,
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt || title || 'Lesson illustration'}
      loading="lazy"
      sx={{ display: 'block', width: '100%', maxHeight: 420, objectFit: 'cover' }}
    />
    {(alt || title) && (
      <Box component="figcaption" sx={{ px: 2, py: 1.25, color: MUTED, fontSize: 12, lineHeight: 1.5 }}>
        {alt || title}
      </Box>
    )}
  </Box>
);

const VideoEmbed = ({ video }) => {
  if (!video?.embedUrl && !video?.videoId) return null;
  const embedSrc = video.embedUrl || `https://www.youtube.com/embed/${video.videoId}?rel=0&modestbranding=1`;

  return (
    <Box sx={{ mb: 3, borderRadius: CARD_RAD, overflow: 'hidden', border: CARD_BDR, bgcolor: CARD_BG }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: CARD_BDR, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Play size={16} color="#f87171" />
        <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 14, flex: 1 }}>
          {video.title || 'Video lesson'}
        </Typography>
        {video.channel && (
          <Typography sx={{ color: MUTED, fontSize: 11 }}>{video.channel}</Typography>
        )}
      </Box>
      <Box sx={{ position: 'relative', paddingTop: '56.25%', bgcolor: '#000' }}>
        <Box
          component="iframe"
          src={embedSrc}
          title={video.title || 'Embedded lesson video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        />
      </Box>
    </Box>
  );
};

const ProgressRing = ({ pct, color, size = 56 }) => {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  return (
    <svg width={size} height={size} aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} fill="rgba(0,0,0,0.35)" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3"
        strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="cv-ring-text">{pct}%</text>
    </svg>
  );
};

const LessonImageGallery = ({ images = [] }) => {
  const valid = images.filter((img) => img?.url);
  if (!valid.length) return null;
  const gridClass = valid.length === 1 ? 'cv-gallery__grid--1' : valid.length === 2 ? 'cv-gallery__grid--2' : 'cv-gallery__grid--3';

  return (
    <section className="cv-gallery">
      <div className="cv-gallery__header">
        <ImageIcon size={16} color={ACCENT} />
        <h3 className="cv-gallery__title">Visual references ({valid.length})</h3>
      </div>
      <div className={`cv-gallery__grid ${gridClass}`}>
        {valid.map((img, i) => (
          <figure key={i} className={`cv-gallery__item ${i === 0 && valid.length > 2 ? 'cv-gallery__item--featured' : ''}`}>
            <img src={img.url} alt={img.alt || img.caption || 'Lesson illustration'} loading="lazy" />
            {(img.caption || img.photographer) && (
              <figcaption className="cv-gallery__caption">
                {img.caption}
                {img.photographer && (
                  <span className="cv-gallery__credit">
                    {img.photographerUrl ? (
                      <a href={img.photographerUrl} target="_blank" rel="noopener noreferrer">{img.photographer}</a>
                    ) : img.photographer}
                    {img.source ? ` · ${img.source}` : ''}
                  </span>
                )}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </section>
  );
};

const LessonCitations = ({ citations = [] }) => {
  if (!citations.length) return null;
  return (
    <section className="cv-citations">
      <h3 className="cv-citations__title">Sources & further reading</h3>
      <ol className="cv-citations__list">
        {citations.map((cite, i) => {
          const label = typeof cite === 'string' ? cite : cite.title || cite.url || cite.text || `Reference ${i + 1}`;
          const url = typeof cite === 'object' ? cite.url : null;
          return (
            <li key={i} className="cv-citations__item">
              {url ? (
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {label} <ExternalLink size={11} style={{ verticalAlign: 'middle', marginLeft: 2 }} />
                </a>
              ) : label}
            </li>
          );
        })}
      </ol>
    </section>
  );
};

const LessonTOC = ({ headings = [], activeId, onSelect }) => {
  if (headings.length < 2) return null;
  return (
    <nav className="cv-toc-rail" aria-label="On this page">
      <p className="cv-toc-rail__title">On this page</p>
      <ul className="cv-toc-rail__list">
        {headings.map((h) => (
          <li key={h.id}>
            <button
              type="button"
              className={`cv-toc-rail__link ${h.level === 3 ? 'cv-toc-rail__link--h3' : ''} ${activeId === h.id ? 'cv-toc-rail__link--active' : ''}`}
              onClick={() => onSelect(h.id)}
            >
              {h.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

const buildMdComponents = (headingIds = new Map()) => {
  const mkHeading = (level) => ({ children }) => {
    const text = String(children).replace(/\*\*/g, '').trim();
    const id = headingIds.get(text) || `cv-h-${slugify(text)}`;
    const Tag = `h${level}`;
    return <Tag id={id} className="cv-md-heading">{children}</Tag>;
  };
  return {
    code: CodeBlock,
    img: ({ src, alt, title }) => <RichImage src={src} alt={alt} title={title} />,
    h2: mkHeading(2),
    h3: mkHeading(3),
  };
};

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════════ */

const CourseViewer = () => {
  const { courseId } = useParams();
  const navigate     = useNavigate();
  const isDesktop    = useMediaQuery('(min-width:900px)');
  const isWideDesktop = useMediaQuery('(min-width:1200px)');
  const contentRef   = useRef(null);
  const autoGenRef   = useRef('');
  const touchRef     = useRef({ startX: 0, startY: 0 });
  const scrollRAF    = useRef(null);

  /* ── State ─────────────────────────────────────────────────────────────── */
  const [course,            setCourse]            = useState(null);
  const [enrollment,        setEnrollment]        = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState('');

  const [sidebarOpen,       setSidebarOpen]       = useState(false);
  const [expandedSections,  setExpandedSections]  = useState({});
  const [currentLesson,     setCurrentLesson]     = useState(null);
  const [lessonContent,     setLessonContent]     = useState(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [completedLessons,  setCompletedLessons]  = useState(new Set());
  const [quizScores,        setQuizScores]        = useState({});
  const [quizPassedKeys,    setQuizPassedKeys]    = useState(new Set());
  const [assignmentDone,    setAssignmentDone]    = useState(new Set());
  const [capstoneDone,      setCapstoneDone]      = useState(new Set());

  const [sidebarSearch,     setSidebarSearch]     = useState('');
  const [focusMode,         setFocusMode]         = useState(false);
  const [fontSize,          setFontSize]          = useState(15);
  const [noteText,          setNoteText]          = useState('');
  const [showNotes,         setShowNotes]         = useState(false);
  const [bookmarks,         setBookmarks]         = useState(new Set());
  const [noteSaved,         setNoteSaved]         = useState(false);

  const [scrollProgress,    setScrollProgress]    = useState(0);
  const [showCompletion,    setShowCompletion]    = useState(false);
  const [mobileToolsOpen,   setMobileToolsOpen]   = useState(false);
  const [showFloatNav,      setShowFloatNav]      = useState(false);
  const [activeTocId,       setActiveTocId]       = useState('');

  /* ── Effects ───────────────────────────────────────────────────────────── */

  useEffect(() => { loadCourse(); }, [courseId]); // eslint-disable-line

  // Close mobile panels on lesson change
  useEffect(() => {
    setSidebarOpen(false);
    setMobileToolsOpen(false);
    setScrollProgress(0);
  }, [currentLesson]);

  // Keyboard navigation (desktop)
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight') { const n = getNextLesson(); if (n) startLesson(n.sectionIndex, n.lessonIndex); }
      if (e.key === 'ArrowLeft')  { const p = getPrevLesson(); if (p) startLesson(p.sectionIndex, p.lessonIndex); }
      if (e.key === 'f' && !e.metaKey && !e.ctrlKey) setFocusMode(prev => !prev);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }); // eslint-disable-line

  // Load bookmarks
  useEffect(() => {
    try { setBookmarks(new Set(JSON.parse(localStorage.getItem(bookmarkKey(courseId)) || '[]'))); }
    catch { /* ignore */ }
  }, [courseId]);

  // Load note on lesson change
  useEffect(() => {
    if (!currentLesson || !courseId) return;
    setNoteText(localStorage.getItem(noteKey(courseId, currentLesson.sectionIndex, currentLesson.lessonIndex)) || '');
    setNoteSaved(false);
  }, [currentLesson, courseId]);

  /* ── Computed ───────────────────────────────────────────────────────────── */

  const progress = useMemo(() => {
    if (!course?.curriculum) return { completed: 0, total: 0, pct: 0 };
    let total = 0;
    course.curriculum.forEach(s => { total += s.lessons?.length || 0; });
    const completed = completedLessons.size;
    return { completed, total, pct: total > 0 ? Math.round((completed / total) * 100) : 0 };
  }, [course, completedLessons]);

  const sectionProgress = useMemo(() => {
    if (!course?.curriculum) return {};
    const m = {};
    course.curriculum.forEach((sec, si) => {
      const t = sec.lessons?.length || 0;
      const c = sec.lessons?.filter((_, li) => completedLessons.has(`${si}-${li}`)).length || 0;
      m[si] = { c, t, pct: t > 0 ? Math.round((c / t) * 100) : 0 };
    });
    return m;
  }, [course, completedLessons]);

  const lessonInfo = useMemo(() => {
    if (!course?.curriculum || !currentLesson) return { current: 0, total: 0 };
    let total = 0, current = 0, idx = 0;
    course.curriculum.forEach((sec, si) => {
      sec.lessons?.forEach((_, li) => {
        total++;
        if (si === currentLesson.sectionIndex && li === currentLesson.lessonIndex) current = idx + 1;
        idx++;
      });
    });
    return { current, total };
  }, [course, currentLesson]);

  const filteredCurriculum = useMemo(() => {
    if (!course?.curriculum) return [];
    if (!sidebarSearch.trim()) return course.curriculum;
    const q = sidebarSearch.toLowerCase();
    return course.curriculum
      .map(sec => ({ ...sec, lessons: sec.lessons?.filter(l => l.title?.toLowerCase().includes(q)) || [] }))
      .filter(sec => sec.sectionTitle?.toLowerCase().includes(q) || sec.lessons.length > 0);
  }, [course, sidebarSearch]);

  const courseColor = useMemo(
    () => pickCourseColor(course?.category || course?.title || ''),
    [course?.category, course?.title]
  );

  const thumbUrl = useMemo(
    () => (course ? resolveCourseThumbnail(course, API_BASE) : null),
    [course]
  );

  const instructorName = useMemo(() => getInstructorName(course), [course]);

  const resumeLesson = useMemo(() => {
    if (!course?.curriculum) return { sectionIndex: 0, lessonIndex: 0 };
    return getResumeLesson(course.curriculum, completedLessons, courseId);
  }, [course, completedLessons, courseId]);

  const resumeLessonMeta = useMemo(() => {
    if (!course?.curriculum) return null;
    const sec = course.curriculum[resumeLesson.sectionIndex];
    const les = sec?.lessons?.[resumeLesson.lessonIndex];
    if (!les) return null;
    return { sectionTitle: sec.sectionTitle, lessonTitle: les.title, ...resumeLesson };
  }, [course, resumeLesson]);

  const lessonHeadings = useMemo(
    () => extractHeadings(lessonContent?.content || ''),
    [lessonContent?.content]
  );

  const headingIdMap = useMemo(() => {
    const map = new Map();
    lessonHeadings.forEach((h) => { if (!map.has(h.text)) map.set(h.text, h.id); });
    return map;
  }, [lessonHeadings]);

  const mdComponents = useMemo(() => buildMdComponents(headingIdMap), [headingIdMap]);

  /* ── Analytics ─────────────────────────────────────────────────────────── */

  const trackAnalytics = useCallback(async (event, meta = {}, lessonRef = currentLesson) => {
    if (!courseId || !lessonRef) return;
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      await fetch(`${API_BASE}/api/content/course/${courseId}/analytics`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sectionIndex: lessonRef.sectionIndex,
          lessonIndex: lessonRef.lessonIndex,
          event,
          meta,
        }),
      });
    } catch { /* non-blocking */ }
  }, [courseId, currentLesson]);

  const isQuizLesson = useCallback((lesson, content) => {
    return lesson?.type === 'quiz' || content?.contentType === 'quiz' || Boolean(content?.quiz?.questions?.length);
  }, []);

  const lessonRequiresQuizPass = useCallback((lesson, content) => {
    return isQuizLesson(lesson, content) && (content?.quiz?.questions?.length > 0);
  }, [isQuizLesson]);

  const canMarkComplete = useCallback(() => {
    if (!currentLesson || !lessonContent) return false;
    const key = `${currentLesson.sectionIndex}-${currentLesson.lessonIndex}`;
    if (lessonRequiresQuizPass(currentLesson.lesson, lessonContent)) {
      return quizPassedKeys.has(key);
    }
    if (currentLesson.lesson.type === 'assignment') {
      return assignmentDone.has(key);
    }
    if (currentLesson.lesson.isCapstone && currentLesson.lesson.type === 'project') {
      return capstoneDone.has(key);
    }
    return true;
  }, [currentLesson, lessonContent, lessonRequiresQuizPass, quizPassedKeys, assignmentDone, capstoneDone]);

  /* ── Data fetching ─────────────────────────────────────────────────────── */

  const loadCourse = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const courseRes = await fetch(`${API_BASE}/api/courses/${courseId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!courseRes.ok) throw new Error('Failed to load course');
      const courseData = await courseRes.json();
      setCourse(courseData.data?.course || courseData.data || courseData);

      const enrollRes = await fetch(`${API_BASE}/api/enrollments?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      if (enrollRes.ok) {
        const enrollData = await enrollRes.json();
        const ue = enrollData.data?.enrollments?.find(e => e.course?._id === courseId);
        setEnrollment(ue);
        const completed = ue?.progress?.curriculumCompleted
          || ue?.progress?.completedLessons
          || [];
        const normalized = completed.map((item) =>
          typeof item === 'string' ? item : `${item.sectionIndex ?? 0}-${item.lessonIndex ?? 0}`
        );
        setCompletedLessons(new Set(normalized));
        if (ue?.progress?.quizScores) {
          const scores = ue.progress.quizScores instanceof Map
            ? Object.fromEntries(ue.progress.quizScores)
            : ue.progress.quizScores;
          setQuizScores(scores || {});
          const passed = Object.entries(scores || {})
            .filter(([, s]) => s >= 70)
            .map(([k]) => k);
          setQuizPassedKeys(new Set(passed));
        }
      }
      setExpandedSections({ 0: true });
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const generateCourseContent = async () => {
    if (!currentLesson) { toast.error('Select a lesson first!'); return; }
    const { sectionIndex, lessonIndex } = currentLesson;
    setGeneratingContent(true);
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    toast('Generating lesson content…', { icon: '⚙️', duration: 4000 });
    try {
      const res = await fetch(`${API_BASE}/api/content/generate-course-content/${courseId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sectionIndex, lessonIndex }),
      });
      if (!res.ok) throw new Error((await res.json()).message || 'Failed');
      const result = await res.json();
      const data = result.data || {};
      toast.success(
        data.cached
          ? '✅ Loaded from cache!'
          : `✅ Generated with ${data.images?.length || 0} image(s)${data.videoEmbed ? ' + video' : ''} in ${data.generationTime}s!`
      );

      if (data.content) {
        setLessonContent({
          title: data.lessonTitle,
          sectionTitle: course?.curriculum?.[sectionIndex]?.sectionTitle,
          content: stripYoutubeMarkers(data.content),
          duration: course?.curriculum?.[sectionIndex]?.lessons?.[lessonIndex]?.duration,
          type: course?.curriculum?.[sectionIndex]?.lessons?.[lessonIndex]?.type,
          contentType: data.contentType,
          videoEmbed: data.videoEmbed || null,
          images: data.images || [],
          quiz: data.quiz || null,
          audioUrl: data.audioUrl || null,
          audioTranscript: data.audioTranscript || null,
          citations: data.citations || [],
          mediaEnriched: data.mediaEnriched,
        });
      }

      await loadCourse();
      setTimeout(() => startLesson(sectionIndex, lessonIndex), 500);
    } catch { toast.error('Failed to generate content'); }
    finally { setGeneratingContent(false); }
  };

  /* ── Notes & Bookmarks ─────────────────────────────────────────────────── */

  const saveNote = () => {
    if (!currentLesson) return;
    localStorage.setItem(noteKey(courseId, currentLesson.sectionIndex, currentLesson.lessonIndex), noteText);
    setNoteSaved(true);
    toast.success('Note saved', { duration: 1500 });
  };

  const toggleBookmark = (si, li) => {
    const id = `${si}-${li}`;
    const next = new Set(bookmarks);
    next.has(id) ? next.delete(id) : next.add(id);
    setBookmarks(next);
    localStorage.setItem(bookmarkKey(courseId), JSON.stringify([...next]));
  };

  /* ── Lesson navigation ─────────────────────────────────────────────────── */

  const toggleSection = i => setExpandedSections(p => ({ ...p, [i]: !p[i] }));

  const startLesson = useCallback((sectionIndex, lessonIndex) => {
    if (!course?.curriculum?.[sectionIndex]?.lessons?.[lessonIndex]) return;
    const section = course.curriculum[sectionIndex];
    const lesson  = section.lessons[lessonIndex];
    const lessonRef = { sectionIndex, lessonIndex, lesson };
    setCurrentLesson(lessonRef);
    setLessonContent({
      title: lesson.title,
      sectionTitle: section.sectionTitle,
      content: lesson.content?.length > 100 ? stripYoutubeMarkers(lesson.content) : null,
      duration: lesson.duration,
      type: lesson.type,
      contentType: lesson.contentType,
      videoEmbed: lesson.videoEmbed || null,
      images: lesson.images || [],
      quiz: lesson.quiz || null,
      audioUrl: lesson.audioUrl || null,
      audioTranscript: lesson.audioTranscript || null,
      citations: lesson.citations || [],
      mediaEnriched: lesson.mediaEnriched,
    });
    setExpandedSections(p => ({ ...p, [sectionIndex]: true }));
    setShowNotes(false);
    setActiveTocId('');
    if (contentRef.current) contentRef.current.scrollTop = 0;
    localStorage.setItem(resumeKey(courseId), JSON.stringify({ sectionIndex, lessonIndex }));
    trackAnalytics('lesson_start', {}, lessonRef);
  }, [course, courseId, trackAnalytics]);

  useEffect(() => {
    if (!currentLesson || generatingContent) return;
    if (isLabLesson(currentLesson.lesson)) return;
    if (lessonContent?.content) return;
    const key = `${currentLesson.sectionIndex}-${currentLesson.lessonIndex}`;
    if (autoGenRef.current === key) return;
    autoGenRef.current = key;
    generateCourseContent();
  }, [currentLesson]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToHeading = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveTocId(id);
    }
  }, []);

  const handleQuizPass = async (score) => {
    if (!currentLesson) return;
    const lessonId = `${currentLesson.sectionIndex}-${currentLesson.lessonIndex}`;
    setQuizPassedKeys((prev) => new Set([...prev, lessonId]));
    setQuizScores((prev) => ({ ...prev, [lessonId]: score }));
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (enrollment?._id) {
      await fetch(`${API_BASE}/api/enrollments/${enrollment._id}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lessonId, completed: false, score }),
      });
    }
    toast.success(`Quiz passed with ${score}%! You can now mark this lesson complete.`);
  };

  const markLessonComplete = async () => {
    if (!currentLesson) return;
    if (!canMarkComplete()) {
      if (lessonRequiresQuizPass(currentLesson.lesson, lessonContent)) {
        toast.error('Pass the quiz before marking this lesson complete');
      } else if (currentLesson.lesson.type === 'assignment') {
        toast.error('Submit your assignment before marking complete');
      } else if (currentLesson.lesson.isCapstone) {
        toast.error('Submit your capstone before marking complete');
      }
      return;
    }
    try {
      const token    = localStorage.getItem('token') || localStorage.getItem('authToken');
      const lessonId = `${currentLesson.sectionIndex}-${currentLesson.lessonIndex}`;
      const newSet   = new Set([...completedLessons, lessonId]);
      setCompletedLessons(newSet);

      if (enrollment?._id) {
        await fetch(`${API_BASE}/api/enrollments/${enrollment._id}/progress`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            lessonId,
            completed: true,
            score: quizScores[lessonId] || undefined,
          }),
        });
      }
      trackAnalytics('lesson_complete');

      const newPct = progress.total > 0 ? Math.round((newSet.size / progress.total) * 100) : 0;
      celebrate(newPct);

      if (newPct === 100) {
        toast.success('🎓 Course complete!', { duration: 5000 });
        setShowCompletion(true);
      } else {
        toast.success(`Lesson complete · ${newSet.size}/${progress.total}`, { duration: 2000 });
        const next = getNextLesson();
        if (next) setTimeout(() => startLesson(next.sectionIndex, next.lessonIndex), 500);
      }
    } catch { toast.error('Failed to update progress'); }
  };

  const getNextLesson = () => {
    if (!currentLesson || !course) return null;
    const { sectionIndex: si, lessonIndex: li } = currentLesson;
    if (li + 1 < course.curriculum[si].lessons.length) return { sectionIndex: si, lessonIndex: li + 1 };
    if (si + 1 < course.curriculum.length) return { sectionIndex: si + 1, lessonIndex: 0 };
    return null;
  };
  const getPrevLesson = () => {
    if (!currentLesson || !course) return null;
    const { sectionIndex: si, lessonIndex: li } = currentLesson;
    if (li > 0) return { sectionIndex: si, lessonIndex: li - 1 };
    if (si > 0) return { sectionIndex: si - 1, lessonIndex: course.curriculum[si - 1].lessons.length - 1 };
    return null;
  };

  const isCompleted = (si, li) => completedLessons.has(`${si}-${li}`);
  const isCurrent   = (si, li) => currentLesson?.sectionIndex === si && currentLesson?.lessonIndex === li;
  const isMarked    = (si, li) => bookmarks.has(`${si}-${li}`);

  /* ── Touch swipe (mobile) ──────────────────────────────────────────────── */

  const onTouchStart = (e) => {
    touchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!currentLesson) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dy = e.changedTouches[0].clientY - touchRef.current.startY;
    if (Math.abs(dx) > Math.abs(dy) * 1.8 && Math.abs(dx) > 80) {
      if (dx > 0) { const p = getPrevLesson(); if (p) startLesson(p.sectionIndex, p.lessonIndex); }
      else        { const n = getNextLesson(); if (n) startLesson(n.sectionIndex, n.lessonIndex); }
    }
  };

  /* ── Scroll progress ───────────────────────────────────────────────────── */

  const handleScroll = useCallback((e) => {
    if (scrollRAF.current) return;
    scrollRAF.current = requestAnimationFrame(() => {
      const el = e.target;
      const max = el.scrollHeight - el.clientHeight;
      setScrollProgress(max > 0 ? Math.min(100, Math.round((el.scrollTop / max) * 100)) : 100);
      setShowFloatNav(el.scrollTop > 280 && !!currentLesson);
      if (lessonHeadings.length > 1) {
        const offset = 120;
        let active = lessonHeadings[0]?.id || '';
        for (const h of lessonHeadings) {
          const node = document.getElementById(h.id);
          if (node && node.getBoundingClientRect().top <= offset) active = h.id;
        }
        setActiveTocId(active);
      }
      scrollRAF.current = null;
    });
  }, [currentLesson, lessonHeadings]);

  /* ═══════════════════════════════════════════════════════════════════════════
     LOADING
     ═══════════════════════════════════════════════════════════════════════════ */

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: BG, gap: 3 }}>
      <Box sx={{ position: 'relative' }}>
        <CircularProgress size={48} thickness={2} sx={{ color: 'rgba(167,139,250,0.2)' }} />
        <CircularProgress size={48} thickness={2} sx={{ color: ACCENT, position: 'absolute', left: 0, animationDuration: '0.8s' }} />
      </Box>
      <Typography sx={{ color: SECONDARY, fontSize: 14 }}>Loading course…</Typography>
    </Box>
  );

  /* ═══════════════════════════════════════════════════════════════════════════
     ERROR
     ═══════════════════════════════════════════════════════════════════════════ */

  if (error || !course) return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: BG, p: 3 }}>
      <Box sx={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
          <AlertCircle size={28} color="#f87171" />
        </Box>
        <Typography variant="h5" sx={{ color: HEADING, mb: 1, fontWeight: 700 }}>Failed to Load Course</Typography>
        <Typography sx={{ color: SECONDARY, mb: 3, fontSize: 14 }}>{error || 'Something went wrong.'}</Typography>
        <Button startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/studentportal')} variant="contained"
          sx={{ bgcolor: ACCENT_DK, '&:hover': { bgcolor: ACCENT }, textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}>
          Back to Portal
        </Button>
      </Box>
    </Box>
  );

  /* ═══════════════════════════════════════════════════════════════════════════
     DERIVED VALUES
     ═══════════════════════════════════════════════════════════════════════════ */

  const nextLesson     = getNextLesson();
  const prevLesson     = getPrevLesson();
  const isCurrentDone  = currentLesson && isCompleted(currentLesson.sectionIndex, currentLesson.lessonIndex);
  const isCurrentBkd   = currentLesson && isMarked(currentLesson.sectionIndex, currentLesson.lessonIndex);

  /* ═══════════════════════════════════════════════════════════════════════════
     SIDEBAR CONTENT (shared between desktop panel and mobile bottom sheet)
     ═══════════════════════════════════════════════════════════════════════════ */

  const renderSidebar = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: '75vh', md: '100%' }, bgcolor: BG_RAISED }}>
      {/* Drag handle (mobile only) */}
      <Box sx={{ display: { md: 'none' }, pt: 1.5, pb: 0.5, textAlign: 'center' }}>
        <Box sx={{ width: 36, height: 4, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, mx: 'auto' }} />
      </Box>

      {/* Header */}
      <Box sx={{ px: 2.5, pt: { xs: 1, md: 2.5 }, pb: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 15 }}>Course Content</Typography>
          <IconButton onClick={() => setSidebarOpen(false)} size="small"
            sx={{ color: MUTED, display: { md: 'none' }, '&:hover': { color: HEADING } }}>
            <X size={16} />
          </IconButton>
        </Box>

        <div className="cv-sidebar-course">
          <div className="cv-sidebar-course__thumb">
            {thumbUrl ? (
              <img src={thumbUrl} alt="" loading="lazy" />
            ) : (
              <GraduationCap size={22} color={courseColor} />
            )}
          </div>
          <div className="cv-sidebar-course__info">
            <p className="cv-sidebar-course__title">{course.title}</p>
            <p className="cv-sidebar-course__sub">{instructorName}</p>
          </div>
        </div>

        {/* Progress card */}
        <Box sx={{ p: 2, mb: 2, borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(167,139,250,0.04) 100%)',
          border: '1px solid rgba(167,139,250,0.15)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ color: SECONDARY, fontSize: 12, fontWeight: 500 }}>Progress</Typography>
            <Typography sx={{ color: ACCENT, fontSize: 14, fontWeight: 700 }}>{progress.pct}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress.pct}
            sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)',
              '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 3, transition: 'transform 0.5s ease' } }} />
          <Typography sx={{ color: FAINT, fontSize: 11, mt: 1 }}>
            {progress.completed} of {progress.total} lessons
          </Typography>
        </Box>

        {/* Search */}
        <TextField
          size="small" placeholder="Search lessons…"
          value={sidebarSearch} onChange={e => setSidebarSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search size={14} color={FAINT} /></InputAdornment>,
            endAdornment: sidebarSearch ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSidebarSearch('')} sx={{ color: FAINT, p: 0.25 }}><X size={12} /></IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '10px', fontSize: 13, color: SECONDARY, height: 38,
              '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
              '&.Mui-focused fieldset': { borderColor: ACCENT },
            },
          }}
        />
      </Box>

      {/* Curriculum list */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 0.5, pb: 4,
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2 } }}>
        {filteredCurriculum.map((section, si) => {
          const realSi  = course.curriculum.indexOf(section);
          const sp      = sectionProgress[realSi] || { c: 0, t: 0, pct: 0 };
          const allDone = sp.t > 0 && sp.c === sp.t;

          return (
            <Box key={si} sx={{ mb: 0.5 }}>
              {/* Section header */}
              <Box component="button" onClick={() => toggleSection(realSi)}
                sx={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 1.5, bgcolor: 'transparent', border: 'none', cursor: 'pointer',
                  textAlign: 'left', borderRadius: '10px', mx: 0,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                }}>
                {/* Section number / checkmark */}
                <Box sx={{
                  width: 28, height: 28, borderRadius: '8px', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: allDone ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.04)',
                  border: allDone ? '1px solid rgba(74,222,128,0.25)' : '1px solid rgba(255,255,255,0.06)',
                }}>
                  {allDone
                    ? <CheckCircle size={14} color={GREEN} />
                    : <Typography sx={{ color: FAINT, fontSize: 11, fontWeight: 600 }}>{realSi + 1}</Typography>}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    color: allDone ? GREEN : HEADING, fontSize: 13, fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {section.sectionTitle}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography sx={{ color: FAINT, fontSize: 11 }}>{sp.c}/{sp.t}</Typography>
                    {sp.pct > 0 && (
                      <Box sx={{ flex: 1, height: 3, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, maxWidth: 80 }}>
                        <Box sx={{ height: '100%', width: `${sp.pct}%`, bgcolor: allDone ? GREEN : ACCENT, borderRadius: 2, transition: 'width 0.4s ease' }} />
                      </Box>
                    )}
                  </Box>
                </Box>
                <ChevronDown size={14} color={FAINT}
                  style={{ transition: 'transform 0.2s', transform: expandedSections[realSi] ? 'rotate(0)' : 'rotate(-90deg)' }} />
              </Box>

              {/* Lessons */}
              <Collapse in={!!expandedSections[realSi]}>
                <Box sx={{ pl: 1.5, pr: 0.5 }}>
                  {section.lessons?.map((lesson, li) => {
                    const realLi = sidebarSearch ? course.curriculum[realSi]?.lessons?.indexOf(lesson) : li;
                    const active = isCurrent(realSi, realLi);
                    const done   = isCompleted(realSi, realLi);
                    const bkd    = isMarked(realSi, realLi);
                    const lt     = getLessonType(lesson.type);
                    const LtIcon = lt.icon;

                    return (
                      <Box key={li} component="button" onClick={() => startLesson(realSi, realLi)}
                        sx={{
                          width: '100%', display: 'flex', alignItems: 'center', gap: 1.25,
                          px: 1.5, py: 1.25, border: 'none', cursor: 'pointer', textAlign: 'left',
                          borderRadius: '10px', mb: 0.25,
                          bgcolor: active ? 'rgba(167,139,250,0.1)' : 'transparent',
                          '&:hover': { bgcolor: active ? 'rgba(167,139,250,0.14)' : 'rgba(255,255,255,0.03)' },
                          transition: 'background 0.15s',
                        }}>
                        {/* Status icon */}
                        <Box sx={{
                          width: 24, height: 24, borderRadius: '7px', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: done ? 'rgba(74,222,128,0.12)' : active ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.03)',
                          border: done ? '1px solid rgba(74,222,128,0.2)' : active ? '1px solid rgba(167,139,250,0.25)' : '1px solid rgba(255,255,255,0.05)',
                        }}>
                          {done ? <CheckCircle size={12} color={GREEN} />
                            : active ? <Play size={11} color={ACCENT} fill={ACCENT} />
                            : <Circle size={12} color={FAINT} />}
                        </Box>
                        {/* Lesson info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{
                            color: active ? HEADING : done ? '#86efac' : BODY,
                            fontSize: 13, fontWeight: active ? 600 : 400, lineHeight: 1.4,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          }}>
                            {lesson.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.3 }}>
                            <LtIcon size={9} color={lt.color} />
                            <Typography sx={{ color: FAINT, fontSize: 10 }}>{lt.label}</Typography>
                            <Typography sx={{ color: FAINT, fontSize: 10 }}>·</Typography>
                            <Typography sx={{ color: FAINT, fontSize: 10 }}>{lesson.duration}m</Typography>
                            {bkd && <Bookmark size={9} color={GOLD} fill={GOLD} />}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          );
        })}

        {filteredCurriculum.length === 0 && sidebarSearch && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Search size={24} color={FAINT} style={{ marginBottom: 8 }} />
            <Typography sx={{ color: FAINT, fontSize: 13 }}>No results for "{sidebarSearch}"</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  /* ═══════════════════════════════════════════════════════════════════════════
     NEXT LESSON PREVIEW CARD
     ═══════════════════════════════════════════════════════════════════════════ */

  const renderNextPreview = () => {
    if (!nextLesson || !course) return null;
    const sec = course.curriculum[nextLesson.sectionIndex];
    const les = sec?.lessons?.[nextLesson.lessonIndex];
    if (!les) return null;
    const lt = getLessonType(les.type);
    const LtIcon = lt.icon;

    return (
      <Box
        onClick={() => startLesson(nextLesson.sectionIndex, nextLesson.lessonIndex)}
        sx={{
          mt: 4, p: { xs: 2, md: 2.5 }, cursor: 'pointer',
          bgcolor: 'rgba(255,255,255,0.02)', border: CARD_BDR, borderRadius: CARD_RAD,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', borderColor: 'rgba(167,139,250,0.25)' },
          transition: 'all 0.2s',
        }}>
        <Typography sx={{ color: FAINT, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, mb: 1.25 }}>
          Up Next
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
            bgcolor: `${lt.color}12`, border: `1px solid ${lt.color}25`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <LtIcon size={18} color={lt.color} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: HEADING, fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{les.title}</Typography>
            <Typography sx={{ color: FAINT, fontSize: 12, mt: 0.25 }}>{les.duration} min · {lt.label}</Typography>
          </Box>
          <ChevronRight size={18} color={FAINT} />
        </Box>
      </Box>
    );
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     NOTES PANEL
     ═══════════════════════════════════════════════════════════════════════════ */

  const renderNotes = () => (
    <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: 'rgba(251,191,36,0.03)', border: '1px solid rgba(251,191,36,0.12)', borderRadius: CARD_RAD }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <StickyNote size={15} color={GOLD} />
        <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 14 }}>My Notes</Typography>
        <Box sx={{ flex: 1 }} />
        {noteSaved && <Typography sx={{ color: GREEN, fontSize: 11 }}>✓ Saved</Typography>}
      </Box>
      <TextField
        multiline rows={4} placeholder="Write your notes…"
        value={noteText} onChange={e => { setNoteText(e.target.value); setNoteSaved(false); }} fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '10px', color: SECONDARY, fontSize: 14,
            '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
            '&.Mui-focused fieldset': { borderColor: 'rgba(251,191,36,0.4)' },
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, gap: 1 }}>
        <Button size="small" onClick={() => setNoteText('')} sx={{ color: MUTED, textTransform: 'none' }}>Clear</Button>
        <Button size="small" variant="contained" onClick={saveNote}
          sx={{ bgcolor: 'rgba(251,191,36,0.15)', color: GOLD, border: '1px solid rgba(251,191,36,0.25)',
            '&:hover': { bgcolor: 'rgba(251,191,36,0.25)' }, textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none' }}>
          Save
        </Button>
      </Box>
    </Box>
  );

  /* ═══════════════════════════════════════════════════════════════════════════
     MARKDOWN STYLES
     ═══════════════════════════════════════════════════════════════════════════ */

  const markdownSx = {
    color: BODY, lineHeight: 1.9, fontSize,
    '& h1, & h2, & h3, & h4, & h5, & h6': { color: HEADING, fontWeight: 700, mt: 4, mb: 1.5 },
    '& h1': { fontSize: '1.75rem' }, '& h2': { fontSize: '1.4rem' }, '& h3': { fontSize: '1.15rem' },
    '& p': { mb: 2 }, '& ul, & ol': { pl: 3, mb: 2 }, '& li': { mb: 0.75 },
    '& a': { color: ACCENT, textDecoration: 'underline', textUnderlineOffset: '3px' },
    '& blockquote': {
      borderLeft: `3px solid rgba(167,139,250,0.4)`, pl: 2.5, py: 1, my: 2.5,
      color: SECONDARY, bgcolor: 'rgba(167,139,250,0.04)', borderRadius: '0 12px 12px 0',
    },
    '& code': {
      bgcolor: 'rgba(255,255,255,0.07)', px: 0.75, py: 0.25,
      borderRadius: '5px', fontSize: '0.84em', color: '#f9a8d4', fontFamily: 'monospace',
    },
    '& table': { width: '100%', borderCollapse: 'collapse', mb: 2.5, overflow: 'auto', display: 'block' },
    '& th': { bgcolor: 'rgba(255,255,255,0.05)', color: HEADING, fontWeight: 700, px: 2, py: 1.25, textAlign: 'left', borderBottom: CARD_BDR, whiteSpace: 'nowrap' },
    '& td': { px: 2, py: 1.1, borderBottom: '1px solid rgba(255,255,255,0.04)', color: SECONDARY },
    '& hr': { border: 'none', borderTop: CARD_BDR, my: 4 },
    '& strong': { color: HEADING, fontWeight: 600 },
    '& img': { maxWidth: '100%', borderRadius: '12px', my: 2.5, boxShadow: '0 8px 32px rgba(0,0,0,0.35)' },
    '& figure': { my: 2.5 },
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════════════════ */

  return (
    <Box className="cv-root" sx={{ height: '100vh', bgcolor: BG, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Course progress stripe (top) ────────────────────────────────────── */}
      <LinearProgress variant="determinate" value={progress.pct}
        sx={{ height: 3, flexShrink: 0, bgcolor: 'rgba(255,255,255,0.03)',
          '& .MuiLinearProgress-bar': { bgcolor: progress.pct === 100 ? GREEN : ACCENT, transition: 'transform 0.5s ease' } }} />

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box component="header" sx={{
        bgcolor: 'rgba(9,9,11,0.97)', borderBottom: CARD_BDR,
        position: 'sticky', top: 0, zIndex: 1100, flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 1.5 }, px: { xs: 1.25, md: 2.5 }, height: { xs: 52, md: 56 } }}>
          {/* Back */}
          <Tooltip title="Back to portal">
            <IconButton onClick={() => navigate('/studentportal')} size="small"
              sx={{ color: SECONDARY, '&:hover': { color: HEADING, bgcolor: 'rgba(255,255,255,0.06)' } }}>
              <ArrowLeft size={18} />
            </IconButton>
          </Tooltip>

          {/* Title area */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              color: HEADING, fontWeight: 700, fontSize: { xs: 13, md: 15 },
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {course.title}
            </Typography>
            {/* Desktop breadcrumb */}
            {currentLesson && isDesktop && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.15 }}>
                <Typography sx={{ color: FAINT, fontSize: 11 }}>{lessonContent?.sectionTitle}</Typography>
                <ChevronRight size={10} color={FAINT} />
                <Typography sx={{ color: MUTED, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                  {currentLesson.lesson.title}
                </Typography>
                <Typography sx={{ color: FAINT, fontSize: 11 }}>({lessonInfo.current}/{lessonInfo.total})</Typography>
              </Box>
            )}
          </Box>

          {/* Mobile progress pill */}
          {!isDesktop && (
            <Box sx={{
              px: 1.25, py: 0.35, borderRadius: '10px', flexShrink: 0,
              bgcolor: progress.pct === 100 ? 'rgba(74,222,128,0.12)' : 'rgba(167,139,250,0.12)',
              border: progress.pct === 100 ? '1px solid rgba(74,222,128,0.25)' : '1px solid rgba(167,139,250,0.25)',
            }}>
              <Typography sx={{ color: progress.pct === 100 ? GREEN : ACCENT, fontSize: 11, fontWeight: 700 }}>
                {progress.pct}%
              </Typography>
            </Box>
          )}

          {/* Desktop tools */}
          {isDesktop && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '8px', px: 0.5, border: '1px solid rgba(255,255,255,0.06)' }}>
                <IconButton size="small" onClick={() => setFontSize(f => Math.max(13, f - 1))} sx={{ color: MUTED, '&:hover': { color: HEADING } }}>
                  <ZoomOut size={14} />
                </IconButton>
                <Typography sx={{ color: FAINT, fontSize: 10, minWidth: 18, textAlign: 'center' }}>{fontSize}</Typography>
                <IconButton size="small" onClick={() => setFontSize(f => Math.min(20, f + 1))} sx={{ color: MUTED, '&:hover': { color: HEADING } }}>
                  <ZoomIn size={14} />
                </IconButton>
              </Box>

              <Tooltip title={focusMode ? 'Exit focus mode' : 'Focus mode (F)'}>
                <IconButton size="small" onClick={() => setFocusMode(f => !f)}
                  sx={{ color: focusMode ? ACCENT : MUTED, '&:hover': { color: HEADING } }}>
                  {focusMode ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </IconButton>
              </Tooltip>

              {currentLesson && (
                <>
                  <Tooltip title={isCurrentBkd ? 'Bookmarked' : 'Bookmark'}>
                    <IconButton size="small" onClick={() => toggleBookmark(currentLesson.sectionIndex, currentLesson.lessonIndex)}
                      sx={{ color: isCurrentBkd ? GOLD : MUTED, '&:hover': { color: GOLD } }}>
                      {isCurrentBkd ? <Bookmark size={16} fill={GOLD} /> : <BookmarkPlus size={16} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Notes">
                    <IconButton size="small" onClick={() => setShowNotes(n => !n)}
                      sx={{ color: showNotes ? ACCENT : MUTED, '&:hover': { color: HEADING } }}>
                      <StickyNote size={16} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          )}

          {/* Desktop progress ring */}
          {isDesktop && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 0.5 }}>
              <Box sx={{ position: 'relative', width: 36, height: 36 }}>
                <svg width="36" height="36" viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2.5" />
                  <circle cx="18" cy="18" r="14" fill="none" stroke={progress.pct === 100 ? GREEN : ACCENT}
                    strokeWidth="2.5" strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 14}`}
                    strokeDashoffset={`${2 * Math.PI * 14 * (1 - progress.pct / 100)}`}
                    style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
                </svg>
                <Typography sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: progress.pct === 100 ? GREEN : ACCENT }}>
                  {progress.pct}%
                </Typography>
              </Box>
            </Box>
          )}

          {/* Mobile hamburger */}
          {!isDesktop && (
            <IconButton onClick={() => setSidebarOpen(true)} size="small"
              sx={{ color: SECONDARY, '&:hover': { color: HEADING } }}>
              <Menu size={20} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* ── Lesson reading progress (thin bar under header) ─────────────────── */}
      {currentLesson && (
        <LinearProgress variant="determinate" value={scrollProgress}
          sx={{ height: 2, flexShrink: 0, bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': { bgcolor: 'rgba(167,139,250,0.35)', transition: 'none' } }} />
      )}

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Mobile bottom-sheet sidebar */}
        <SwipeableDrawer
          anchor="bottom" open={sidebarOpen}
          onClose={() => setSidebarOpen(false)} onOpen={() => setSidebarOpen(true)}
          disableSwipeToOpen
          sx={{
            display: { md: 'none' },
            '& .MuiDrawer-paper': {
              bgcolor: BG_RAISED, borderRadius: '20px 20px 0 0',
              maxHeight: '82vh', border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)',
            },
          }}
        >
          {renderSidebar()}
        </SwipeableDrawer>

        {/* Desktop permanent sidebar */}
        {isDesktop && !focusMode && (
          <Box component="aside" sx={{
            width: SIDEBAR_W, flexShrink: 0, borderRight: CARD_BDR, overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 0 },
          }}>
            {renderSidebar()}
          </Box>
        )}

        {/* ── Main content ──────────────────────────────────────────────────── */}
        <Box
          component="main"
          ref={contentRef}
          onScroll={handleScroll}
          onTouchStart={!isDesktop ? onTouchStart : undefined}
          onTouchEnd={!isDesktop ? onTouchEnd : undefined}
          sx={{
            flex: 1, overflow: 'auto', bgcolor: BG,
            display: 'flex', flexDirection: 'column',
            pb: { xs: currentLesson ? '90px' : 0, md: 0 },
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 3 },
          }}
        >
          <AnimatePresence mode="wait">
            {!currentLesson ? (
              /* ══════════════════════════════════════════════════════════════════
                 WELCOME SCREEN
                 ══════════════════════════════════════════════════════════════════ */
              <motion.div
                key="welcome"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ flex: 1 }}
              >
                <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 860, mx: 'auto', width: '100%' }}>
                  {/* Cinematic hero */}
                  <section className="cv-hero">
                    <div className="cv-hero__media" style={{ background: `linear-gradient(135deg, ${courseColor}44 0%, rgba(9,9,11,0.95) 70%)` }}>
                      {thumbUrl && <img src={thumbUrl} alt="" loading="lazy" />}
                      <div className="cv-hero__overlay" />
                    </div>
                    <div className="cv-hero__content">
                      <div className="cv-hero__top">
                        <div className="cv-hero__badges">
                          {course.category && (
                            <span className="cv-hero__badge">{formatCategoryLabel(course.category)}</span>
                          )}
                          {(course.aiGenerated || course.isAIGenerated || course.metadata?.generatedBy) && (
                            <span className="cv-hero__badge cv-hero__badge--ai"><Sparkles size={11} /> Studio course</span>
                          )}
                          <span className="cv-hero__badge">{course.level || 'All levels'}</span>
                        </div>
                        <div className="cv-hero__ring-wrap">
                          <ProgressRing pct={progress.pct} color={progress.pct === 100 ? GREEN : courseColor} size={isDesktop ? 64 : 56} />
                        </div>
                      </div>
                      <h1 className="cv-hero__title">{course.title}</h1>
                      {course.tagline && (
                        <p className="cv-hero__tagline" style={{ color: GOLD, fontWeight: 650, fontSize: 15, margin: '6px 0 8px' }}>
                          {course.tagline}
                        </p>
                      )}
                      <p className="cv-hero__instructor"><User size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{instructorName}</p>
                      {course.description && <p className="cv-hero__desc">{course.description}</p>}
                      {course.metadata?.capstone?.title && (
                        <p className="cv-hero__desc" style={{ color: ACCENT, marginTop: 8 }}>
                          Capstone: {course.metadata.capstone.title}
                          {course.metadata.capstone.brief ? ` — ${course.metadata.capstone.brief}` : ''}
                        </p>
                      )}
                      <div className="cv-hero__stats">
                        <div className="cv-hero__stat"><strong>{progress.total}</strong><span>Lessons</span></div>
                        <div className="cv-hero__stat"><strong>{formatCourseDuration(course.totalDuration)}</strong><span>Duration</span></div>
                        <div className="cv-hero__stat"><strong>{course.curriculum?.length || 0}</strong><span>Sections</span></div>
                        <div className="cv-hero__stat"><strong>{progress.completed}</strong><span>Completed</span></div>
                      </div>
                    </div>
                  </section>

                  {/* Section journey */}
                  {course.curriculum?.length > 0 && (
                    <section className="cv-journey">
                      <h2 className="cv-journey__title">Your learning path</h2>
                      <div className="cv-journey__track">
                        {course.curriculum.map((sec, si) => {
                          const sp = sectionProgress[si] || { c: 0, t: 0, pct: 0 };
                          const allDone = sp.t > 0 && sp.c === sp.t;
                          const isActive = resumeLesson.sectionIndex === si && !allDone;
                          const firstLesson = sec.lessons?.findIndex((_, li) => !isCompleted(si, li));
                          const targetLi = firstLesson >= 0 ? firstLesson : 0;
                          return (
                            <button
                              key={si}
                              type="button"
                              className={`cv-journey__section ${allDone ? 'cv-journey__section--done' : ''} ${isActive ? 'cv-journey__section--active' : ''}`}
                              onClick={() => startLesson(si, targetLi)}
                            >
                              <span className="cv-journey__dot">{allDone ? '✓' : si + 1}</span>
                              <span className="cv-journey__name">{sec.sectionTitle}</span>
                              <span className="cv-journey__meta">{sp.c}/{sp.t}</span>
                              <div className="cv-journey__bar">
                                <div className="cv-journey__bar-fill" style={{ width: `${sp.pct}%`, background: allDone ? GREEN : courseColor }} />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  {course.skills?.length > 0 && (
                    <Box sx={{ p: { xs: 2, md: 2.5 }, mb: 2, bgcolor: CARD_BG, border: CARD_BDR, borderRadius: CARD_RAD }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Target size={16} color={courseColor} />
                        <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 14 }}>Skills you'll build</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {course.skills.map((skill, i) => (
                          <Chip key={i} label={skill.name} size="small"
                            sx={{ bgcolor: `${courseColor}12`, color: BODY, border: `1px solid ${courseColor}30`, fontSize: 12, height: 28 }} />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {course.objectives?.length > 0 && (
                    <Box sx={{ p: { xs: 2, md: 2.5 }, mb: 2, bgcolor: CARD_BG, border: CARD_BDR, borderRadius: CARD_RAD }}>
                      <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 14, mb: 1.5 }}>What you'll learn</Typography>
                      {course.objectives.map((obj, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 1 }}>
                          <CheckCircle size={14} color={GREEN} style={{ flexShrink: 0, marginTop: 2 }} />
                          <Typography sx={{ color: SECONDARY, fontSize: 13, lineHeight: 1.6 }}>{obj}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <div className="cv-cta-row">
                    <Button
                      startIcon={<Play size={18} fill={HEADING} />}
                      onClick={() => startLesson(resumeLesson.sectionIndex, resumeLesson.lessonIndex)}
                      variant="contained"
                      fullWidth={!isDesktop}
                      sx={{
                        background: `linear-gradient(135deg, ${courseColor} 0%, ${courseColor}cc 100%)`,
                        '&:hover': { filter: 'brightness(1.08)' },
                        textTransform: 'none', borderRadius: '12px', px: { xs: 3, md: 5 }, py: 1.5,
                        fontWeight: 700, fontSize: { xs: 15, md: 16 },
                        boxShadow: `0 4px 24px ${courseColor}55`,
                        flex: isDesktop ? '0 1 auto' : undefined,
                      }}
                    >
                      {progress.pct === 100 ? 'Review course' : progress.pct > 0 ? 'Continue learning' : 'Start learning'}
                    </Button>
                    {progress.pct > 0 && progress.pct < 100 && (
                      <Button
                        startIcon={<List size={16} />}
                        onClick={() => setSidebarOpen(true)}
                        variant="outlined"
                        fullWidth={!isDesktop}
                        sx={{
                          color: SECONDARY, borderColor: 'rgba(255,255,255,0.12)',
                          '&:hover': { borderColor: 'rgba(255,255,255,0.25)', bgcolor: 'rgba(255,255,255,0.03)' },
                          textTransform: 'none', borderRadius: '12px', fontWeight: 600,
                          display: { md: 'none' },
                        }}
                      >
                        Browse lessons
                      </Button>
                    )}
                  </div>
                  {resumeLessonMeta && progress.pct > 0 && progress.pct < 100 && (
                    <p className="cv-cta-row__hint">
                      Resume at <strong style={{ color: HEADING }}>{resumeLessonMeta.lessonTitle}</strong>
                      {' · '}{resumeLessonMeta.sectionTitle}
                    </p>
                  )}
                </Box>
              </motion.div>

            ) : (
              /* ══════════════════════════════════════════════════════════════════
                 LESSON VIEW
                 ══════════════════════════════════════════════════════════════════ */
              <motion.div
                key={`${currentLesson.sectionIndex}-${currentLesson.lessonIndex}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.15 }}
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2.5, md: 4 }, width: '100%', mx: 'auto', maxWidth: isWideDesktop && lessonHeadings.length > 1 ? 1100 : 820 }}>

                  <div className="cv-lesson-layout">
                    <div className="cv-lesson-main">
                      <article className="cv-lesson-article">
                        <div className="cv-lesson-header-card">
                          {!isDesktop && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                              <Typography sx={{ color: FAINT, fontSize: 11 }}>{lessonContent?.sectionTitle}</Typography>
                              <ChevronRight size={10} color={FAINT} />
                              <Typography sx={{ color: MUTED, fontSize: 11 }}>
                                Lesson {lessonInfo.current} of {lessonInfo.total}
                              </Typography>
                            </Box>
                          )}
                          <h1 className="cv-lesson-title">{currentLesson.lesson.title}</h1>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            {(() => {
                              const lt = getLessonType(currentLesson.lesson.type);
                              const LtIcon = lt.icon;
                              return (
                                <Chip icon={<LtIcon size={11} />} label={lt.label} size="small"
                                  sx={{ height: 26, bgcolor: `${lt.color}10`, color: lt.color, border: `1px solid ${lt.color}25`, '& .MuiChip-icon': { color: lt.color }, fontSize: 11 }} />
                              );
                            })()}
                            <Chip icon={<Clock size={10} />} label={`${currentLesson.lesson.duration} min`} size="small"
                              sx={{ height: 26, bgcolor: 'rgba(255,255,255,0.04)', color: SECONDARY, border: CARD_BDR, '& .MuiChip-icon': { color: MUTED }, fontSize: 11 }} />
                            {lessonContent?.content && (
                              <Chip icon={<BookOpen size={10} />} label={`~${readingTime(lessonContent.content)} min read`} size="small"
                                sx={{ height: 26, bgcolor: 'rgba(255,255,255,0.04)', color: SECONDARY, border: CARD_BDR, '& .MuiChip-icon': { color: MUTED }, fontSize: 11 }} />
                            )}
                            {scrollProgress > 5 && (
                              <Chip label={`${scrollProgress}% read`} size="small"
                                sx={{ height: 26, bgcolor: 'rgba(167,139,250,0.08)', color: ACCENT, border: '1px solid rgba(167,139,250,0.2)', fontSize: 11 }} />
                            )}
                            {isCurrentDone && (
                              <Chip icon={<CheckCircle size={10} />} label="Completed" size="small"
                                sx={{ height: 26, bgcolor: 'rgba(74,222,128,0.08)', color: GREEN, border: '1px solid rgba(74,222,128,0.2)', '& .MuiChip-icon': { color: GREEN }, fontSize: 11 }} />
                            )}
                          </Box>
                        </div>

                  {/* ── Lesson body ─────────────────────────────────────────────── */}
                  {isLabLesson(currentLesson.lesson) && !lessonContent?.content ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: { xs: 6, md: 10 }, textAlign: 'center' }}>
                      <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: { xs: 17, md: 20 }, mb: 1 }}>
                        This is the floor
                      </Typography>
                      <Typography sx={{ color: SECONDARY, fontSize: 14, mb: 3, maxWidth: 420, lineHeight: 1.7 }}>
                        Not generated. Open the bench — warpage, peel, or defence — and do the work.
                      </Typography>
                      <Button
                        startIcon={<FlaskConical size={16} />}
                        onClick={() => navigate(benchPath(currentLesson.lesson.labId || 'semicon-mech', currentLesson.lesson.bench || 'warpage', {
                          courseId,
                          sectionIndex: currentLesson.sectionIndex,
                          lessonIndex: currentLesson.lessonIndex,
                        }))}
                        variant="contained"
                        sx={{
                          background: 'linear-gradient(135deg, #22d3ee 0%, #6366f1 100%)',
                          color: '#041018',
                          textTransform: 'none', borderRadius: '12px', fontWeight: 700, px: 4, py: 1.25,
                        }}
                      >
                        Open bench
                      </Button>
                    </Box>
                  ) : lessonContent?.content || lessonContent?.quiz ? (
                    <>
                      {lessonContent.audioUrl && (
                        <AudioLessonPlayer
                          audioUrl={lessonContent.audioUrl}
                          transcript={lessonContent.audioTranscript}
                          title={currentLesson.lesson.title}
                        />
                      )}

                      {lessonContent.videoEmbed && (
                        <VideoEmbed video={lessonContent.videoEmbed} />
                      )}

                      <LessonImageGallery images={lessonContent.images} />

                      {lessonContent.content && (
                        <Box sx={markdownSx}>
                          <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]} components={mdComponents}>
                            {lessonContent.content}
                          </ReactMarkdown>
                        </Box>
                      )}

                      <LessonCitations citations={lessonContent.citations} />

                      {lessonContent.quiz?.questions?.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <QuizLesson
                            quiz={lessonContent.quiz}
                            onPass={handleQuizPass}
                            onAnalytics={trackAnalytics}
                          />
                        </Box>
                      )}

                      {currentLesson.lesson.type === 'assignment' && (
                        <AssignmentSubmission
                          courseId={courseId}
                          sectionIndex={currentLesson.sectionIndex}
                          lessonIndex={currentLesson.lessonIndex}
                          onSubmitted={() => {
                            const key = `${currentLesson.sectionIndex}-${currentLesson.lessonIndex}`;
                            setAssignmentDone((prev) => new Set([...prev, key]));
                            trackAnalytics('assignment_submit');
                          }}
                        />
                      )}

                      {currentLesson.lesson.isCapstone && currentLesson.lesson.type === 'project' && (
                        <CapstoneSubmission
                          courseId={courseId}
                          sectionIndex={currentLesson.sectionIndex}
                          lessonIndex={currentLesson.lessonIndex}
                          onSubmitted={() => {
                            const key = `${currentLesson.sectionIndex}-${currentLesson.lessonIndex}`;
                            setCapstoneDone((prev) => new Set([...prev, key]));
                            trackAnalytics('assignment_submit');
                          }}
                        />
                      )}

                      {/* Assignment callout */}
                      {currentLesson.lesson.type === 'assignment' && !assignmentDone.has(`${currentLesson.sectionIndex}-${currentLesson.lessonIndex}`) && (
                        <Box sx={{ display: 'flex', gap: 2, p: { xs: 2, md: 2.5 }, bgcolor: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.15)', borderRadius: CARD_RAD, mt: 3 }}>
                          <HelpCircle size={20} color={GOLD} style={{ flexShrink: 0, marginTop: 2 }} />
                          <Box>
                            <Typography sx={{ color: HEADING, fontWeight: 700, mb: 0.5, fontSize: 14 }}>Practice Exercise</Typography>
                            <Typography sx={{ color: SECONDARY, fontSize: 13, lineHeight: 1.7 }}>Apply what you've learned, then submit above.</Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Key concepts */}
                      {course.curriculum[currentLesson.sectionIndex]?.keyConcepts?.length > 0 && (
                        <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: CARD_BG, border: CARD_BDR, borderRadius: CARD_RAD, mt: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <Lightbulb size={16} color={GOLD} />
                            <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 14 }}>Key Concepts</Typography>
                          </Box>
                          {course.curriculum[currentLesson.sectionIndex].keyConcepts.map((c, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 0.75 }}>
                              <Lightbulb size={12} color={GOLD} style={{ flexShrink: 0, marginTop: 3 }} />
                              <Typography sx={{ color: SECONDARY, fontSize: 13 }}>{c}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Ask QoreAI */}
                      <Box sx={{
                        mt: 3, p: { xs: 2, md: 2.5 }, borderRadius: CARD_RAD,
                        bgcolor: 'rgba(167,139,250,0.03)', border: '1px solid rgba(167,139,250,0.12)',
                        display: 'flex', alignItems: { xs: 'flex-start', md: 'center' },
                        flexDirection: { xs: 'column', md: 'row' }, gap: 2,
                      }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ color: HEADING, fontWeight: 600, fontSize: 14 }}>Questions?</Typography>
                          <Typography sx={{ color: MUTED, fontSize: 13, mt: 0.25 }}>Ask QoreAI for deeper explanations or examples.</Typography>
                        </Box>
                        <Button startIcon={<MessageSquare size={15} />} onClick={() => navigate('/aitutoring')} variant="outlined" size="small"
                          sx={{
                            color: ACCENT, borderColor: 'rgba(167,139,250,0.3)', flexShrink: 0,
                            '&:hover': { borderColor: ACCENT, bgcolor: 'rgba(167,139,250,0.06)' },
                            textTransform: 'none', borderRadius: '8px', fontWeight: 600,
                            alignSelf: { xs: 'stretch', md: 'center' },
                          }}>
                          Ask QoreAI
                        </Button>
                      </Box>

                      {/* Notes (toggle) */}
                      {showNotes && <Box sx={{ mt: 3 }}>{renderNotes()}</Box>}

                      {/* Next lesson preview */}
                      {renderNextPreview()}

                      {/* Desktop footer nav */}
                      {isDesktop && (
                        <Box sx={{ borderTop: CARD_BDR, mt: 4, pt: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                          <Button startIcon={<ChevronLeft size={16} />}
                            onClick={() => { if (prevLesson) startLesson(prevLesson.sectionIndex, prevLesson.lessonIndex); }}
                            disabled={!prevLesson} variant="outlined" size="small"
                            sx={{ color: SECONDARY, borderColor: 'rgba(255,255,255,0.1)', '&:hover': { borderColor: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.03)' }, '&.Mui-disabled': { color: FAINT, borderColor: 'rgba(255,255,255,0.04)' }, textTransform: 'none', borderRadius: '10px' }}>
                            Previous
                          </Button>

                          <Button
                            startIcon={isCurrentDone ? <CheckCircle size={16} /> : <Trophy size={16} />}
                            onClick={markLessonComplete}
                            disabled={isCurrentDone || !canMarkComplete()}
                            variant="contained"
                            sx={{
                              bgcolor: isCurrentDone ? 'rgba(74,222,128,0.15)' : !canMarkComplete() ? 'rgba(255,255,255,0.06)' : GREEN,
                              color: isCurrentDone ? 'rgba(74,222,128,0.5)' : !canMarkComplete() ? MUTED : '#052e16',
                              '&:hover': { bgcolor: canMarkComplete() && !isCurrentDone ? '#22c55e' : undefined },
                              '&.Mui-disabled': { bgcolor: 'rgba(74,222,128,0.12)', color: 'rgba(74,222,128,0.4)' },
                              textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3,
                            }}>
                            {isCurrentDone ? 'Completed' : !canMarkComplete() ? 'Complete Requirements First' : 'Mark Complete'}
                          </Button>

                          <Button endIcon={<ChevronRight size={16} />}
                            onClick={() => { if (nextLesson) startLesson(nextLesson.sectionIndex, nextLesson.lessonIndex); }}
                            disabled={!nextLesson} variant="outlined" size="small"
                            sx={{ color: SECONDARY, borderColor: 'rgba(255,255,255,0.1)', '&:hover': { borderColor: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.03)' }, '&.Mui-disabled': { color: FAINT, borderColor: 'rgba(255,255,255,0.04)' }, textTransform: 'none', borderRadius: '10px' }}>
                            Next
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    /* ── No content — generate ───────────────────────────────────── */
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: { xs: 6, md: 10 }, textAlign: 'center' }}>
                      <Box sx={{
                        width: 88, height: 88, borderRadius: '50%', mb: 3,
                        background: generatingContent
                          ? 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(167,139,250,0.06) 100%)'
                          : 'rgba(255,255,255,0.03)',
                        border: generatingContent ? '1px solid rgba(167,139,250,0.25)' : CARD_BDR,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {generatingContent
                          ? <CircularProgress size={36} thickness={2} sx={{ color: ACCENT }} />
                          : <Sparkles size={36} color={ACCENT} />}
                      </Box>
                      <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: { xs: 17, md: 20 }, mb: 1 }}>
                        {generatingContent ? 'Studio is writing this lesson…' : 'This lesson is not written yet'}
                      </Typography>
                      <Typography sx={{ color: SECONDARY, fontSize: 14, mb: generatingContent ? 0 : 3, maxWidth: 420, lineHeight: 1.7 }}>
                        {generatingContent
                          ? 'A scene from the job, one mental model, a worked example, and a checkpoint — built for this course, not scraped from a textbook.'
                          : 'Open it and QoreNova Studio writes a full lesson for this module, including a quiz from the content itself.'}
                      </Typography>
                      {!generatingContent && (
                        <Button startIcon={<Sparkles size={16} />} onClick={generateCourseContent}
                          variant="contained" fullWidth={!isDesktop}
                          sx={{
                            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' },
                            textTransform: 'none', borderRadius: '12px', fontWeight: 700, px: 4, py: 1.25,
                            boxShadow: '0 4px 20px rgba(124,58,237,0.3)',
                            maxWidth: { xs: '100%', md: 300 },
                          }}>
                          Generate This Lesson
                        </Button>
                      )}
                    </Box>
                  )}
                      </article>
                    </div>
                    {isWideDesktop && isDesktop && !focusMode && lessonHeadings.length > 1 && (
                      <LessonTOC headings={lessonHeadings} activeId={activeTocId} onSelect={scrollToHeading} />
                    )}
                  </div>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      {/* Desktop floating lesson nav */}
      {isDesktop && currentLesson && showFloatNav && !focusMode && (
        <div className="cv-float-nav">
          <Button startIcon={<ChevronLeft size={16} />}
            onClick={() => { if (prevLesson) startLesson(prevLesson.sectionIndex, prevLesson.lessonIndex); }}
            disabled={!prevLesson} size="small"
            sx={{ color: SECONDARY, minWidth: 0, textTransform: 'none', borderRadius: '10px', '&.Mui-disabled': { color: FAINT } }}>
            Prev
          </Button>
          <Button
            startIcon={isCurrentDone ? <CheckCircle size={14} /> : <Trophy size={14} />}
            onClick={markLessonComplete}
            disabled={isCurrentDone || !canMarkComplete()}
            size="small"
            sx={{
              bgcolor: isCurrentDone ? 'rgba(74,222,128,0.12)' : !canMarkComplete() ? 'rgba(255,255,255,0.06)' : GREEN,
              color: isCurrentDone ? 'rgba(74,222,128,0.5)' : !canMarkComplete() ? MUTED : '#052e16',
              textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 2,
              '&:hover': { bgcolor: canMarkComplete() && !isCurrentDone ? '#22c55e' : undefined },
            }}>
            {isCurrentDone ? 'Done' : 'Complete'}
          </Button>
          <Button endIcon={<ChevronRight size={16} />}
            onClick={() => { if (nextLesson) startLesson(nextLesson.sectionIndex, nextLesson.lessonIndex); }}
            disabled={!nextLesson} size="small"
            sx={{ color: SECONDARY, minWidth: 0, textTransform: 'none', borderRadius: '10px', '&.Mui-disabled': { color: FAINT } }}>
            Next
          </Button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
         MOBILE BOTTOM NAVIGATION BAR
         ═══════════════════════════════════════════════════════════════════════ */}
      {currentLesson && !isDesktop && (
        <Box sx={{
          flexShrink: 0,
          bgcolor: 'rgba(15,15,18,0.98)', borderTop: '1px solid rgba(255,255,255,0.08)',
          px: 1.5, py: 1.25,
          pb: 'calc(10px + env(safe-area-inset-bottom, 0px))',
          display: 'flex', alignItems: 'center', gap: 1,
          zIndex: 1200,
        }}>
          {/* Prev */}
          <IconButton
            onClick={() => { if (prevLesson) startLesson(prevLesson.sectionIndex, prevLesson.lessonIndex); }}
            disabled={!prevLesson}
            sx={{
              width: 44, height: 44, borderRadius: '12px',
              bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
              color: prevLesson ? SECONDARY : FAINT,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.03)' },
            }}
          >
            <ChevronLeft size={20} />
          </IconButton>

          {/* Mark Complete (center, stretches) */}
          <Button
            onClick={markLessonComplete}
            disabled={isCurrentDone || !canMarkComplete()}
            startIcon={isCurrentDone ? <CheckCircle size={16} /> : <Trophy size={16} />}
            sx={{
              flex: 1, height: 44, borderRadius: '12px', fontWeight: 700, textTransform: 'none', fontSize: 13,
              bgcolor: isCurrentDone ? 'rgba(74,222,128,0.1)' : !canMarkComplete() ? 'rgba(255,255,255,0.06)' : GREEN,
              color: isCurrentDone ? 'rgba(74,222,128,0.4)' : !canMarkComplete() ? MUTED : '#052e16',
              '&:hover': { bgcolor: canMarkComplete() && !isCurrentDone ? '#22c55e' : undefined },
              '&.Mui-disabled': { bgcolor: 'rgba(74,222,128,0.08)', color: 'rgba(74,222,128,0.35)' },
            }}
          >
            {isCurrentDone ? 'Done' : !canMarkComplete() ? 'Requirements' : 'Complete'}
          </Button>

          {/* Next */}
          <IconButton
            onClick={() => { if (nextLesson) startLesson(nextLesson.sectionIndex, nextLesson.lessonIndex); }}
            disabled={!nextLesson}
            sx={{
              width: 44, height: 44, borderRadius: '12px',
              bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
              color: nextLesson ? SECONDARY : FAINT,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.03)' },
            }}
          >
            <ChevronRight size={20} />
          </IconButton>

          {/* Mobile tools */}
          <IconButton
            onClick={() => setMobileToolsOpen(true)}
            sx={{
              width: 44, height: 44, borderRadius: '12px',
              bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
              color: MUTED, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            <MoreHorizontal size={18} />
          </IconButton>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
         MOBILE TOOLS DRAWER
         ═══════════════════════════════════════════════════════════════════════ */}
      <Drawer
        anchor="bottom" open={mobileToolsOpen} onClose={() => setMobileToolsOpen(false)}
        sx={{
          display: { md: 'none' },
          '& .MuiDrawer-paper': {
            bgcolor: BG_RAISED, borderRadius: '20px 20px 0 0',
            maxHeight: '70vh', border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          {/* Drag handle */}
          <Box sx={{ width: 36, height: 4, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, mx: 'auto', mb: 3 }} />

          {/* Font size */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, pb: 2.5, borderBottom: CARD_BDR }}>
            <Typography sx={{ color: HEADING, fontWeight: 600, fontSize: 14 }}>Font Size</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: '10px', px: 1, border: '1px solid rgba(255,255,255,0.06)' }}>
              <IconButton size="small" onClick={() => setFontSize(f => Math.max(13, f - 1))} sx={{ color: SECONDARY }}>
                <ZoomOut size={16} />
              </IconButton>
              <Typography sx={{ color: HEADING, fontSize: 14, fontWeight: 600, minWidth: 24, textAlign: 'center' }}>{fontSize}</Typography>
              <IconButton size="small" onClick={() => setFontSize(f => Math.min(20, f + 1))} sx={{ color: SECONDARY }}>
                <ZoomIn size={16} />
              </IconButton>
            </Box>
          </Box>

          {/* Bookmark */}
          {currentLesson && (
            <Box
              component="button"
              onClick={() => { toggleBookmark(currentLesson.sectionIndex, currentLesson.lessonIndex); }}
              sx={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 2.5, pb: 2.5,
                bgcolor: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                borderBottom: CARD_BDR,
              }}
            >
              {isCurrentBkd
                ? <Bookmark size={20} color={GOLD} fill={GOLD} />
                : <BookmarkPlus size={20} color={SECONDARY} />}
              <Typography sx={{ color: HEADING, fontSize: 14, fontWeight: 500 }}>
                {isCurrentBkd ? 'Bookmarked' : 'Bookmark this lesson'}
              </Typography>
            </Box>
          )}

          {/* Notes */}
          {currentLesson && renderNotes()}
        </Box>
      </Drawer>

      {/* ═══════════════════════════════════════════════════════════════════════
         COURSE COMPLETION OVERLAY
         ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showCompletion && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 2000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(9,9,11,0.92)', backdropFilter: 'blur(12px)',
            }}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15, type: 'spring', stiffness: 200, damping: 20 }}
            >
              <Box sx={{ textAlign: 'center', p: { xs: 3, md: 5 }, maxWidth: 440 }}>
                <Box sx={{
                  width: 96, height: 96, borderRadius: '50%', mx: 'auto', mb: 3,
                  background: 'linear-gradient(135deg, rgba(251,191,36,0.15) 0%, rgba(124,58,237,0.1) 100%)',
                  border: '2px solid rgba(251,191,36,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Award size={48} color={GOLD} />
                </Box>
                <Typography sx={{ color: HEADING, fontWeight: 800, fontSize: { xs: 24, md: 30 }, mb: 1 }}>
                  Course Complete! 🎓
                </Typography>
                <Typography sx={{ color: SECONDARY, fontSize: 15, lineHeight: 1.7, mb: 1 }}>
                  You finished all {progress.total} lessons in
                </Typography>
                <Typography sx={{ color: ACCENT, fontWeight: 700, fontSize: 18, mb: 3 }}>
                  {course.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Button onClick={() => setShowCompletion(false)} variant="outlined"
                    sx={{ color: SECONDARY, borderColor: 'rgba(255,255,255,0.12)', '&:hover': { borderColor: 'rgba(255,255,255,0.25)' }, textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}>
                    Review Lessons
                  </Button>
                  <Button onClick={() => navigate('/studentportal')} variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                      '&:hover': { background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' },
                      textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3,
                      boxShadow: '0 4px 20px rgba(124,58,237,0.3)',
                    }}>
                    Back to Portal
                  </Button>
                </Box>
              </Box>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default CourseViewer;
